import React, { useEffect, useState } from "react";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import { getWalletFee } from "@funds/features/helpers/validation";
import { useFormik } from "formik";
import { Alert, Checkbox, Form, Input, Space, Typography } from "antd";
import {
  WithdrawalProcessSteps,
  useWithdrawalProcess,
} from "../../useWithdrawalProcess";
import BankForm from "./parts/BankForm/BankForm";
import {
  fdtWithdrawalRestrictedCountries,
  fdtConsiderStableCoins,
} from "@funds/components/WarningBox/warningMessages";
import Big from "big.js";
import { getCountryNamesArray } from "@funds/features/helpers/functions";
import BftNotice from "../BftNotice/BftNotice";
import validationSchema from "./validationSchema";
import { FiatFormProps } from "./types";
import AccessRestrictedMessage from "./parts/AccessRestrictedMessage/AccessRestrictedMessage";
import BankAccountOption from "./parts/BankAccountOption/BankAccountOption";
import FundsService from "@services/Funds.service";
import { setBankFormDataAvailable } from "@funds/features/action.funds";
import { TInvestorBankAccountDetails } from "@funds/components/BankForm/IBankForm";
import { useBankAccounts } from "@funds/components/BankAccounts/useBankAccounts";
import NavigationFooter from "../NavigationFooter";
import DangerSVG from "@images/funds/danger.svg";
import { animateScrollTo } from "@features/utils/functions";
import CurrencyAvatar from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyAvatar";
import { avatarBgColor } from "@funds/pages/MyFunds/tabs/FundsTab/table-column";
import { useMediaQuery } from "react-responsive";

import * as style from "./FiatFormStyle.module.scss"

const FiatForm = ({
  currency,
  bankAccounts,
  fiatRestrictedCountries,
  countryOfResidence,
  userWallet,
  bankFormAddressDataAvailable,
  verificationStatus,
  isCompany,
  fees,
  setBankFormDataAvailable,
  investorId,
}: FiatFormProps) => {
  const { state, setState } = useWithdrawalProcess();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const blockedCountries = getCountryNamesArray(fiatRestrictedCountries);
  const isBftCurrency = currency === "bft";
  const [investorAccountDetails, setInvestorAccountDetails] =
    useState<TInvestorBankAccountDetails | null>(null);

  const { values, errors, dispatch } = useBankAccounts();

  useEffect(() => {
    investorId &&
      FundsService.getInvestorIdentityData(investorId).then((res) => {
        setInvestorAccountDetails(res.data);
        setBankFormDataAvailable(res.data.address_valid);
      });

    return () => {
      setBankFormDataAvailable(true);
    };
  }, []);

  const currencyLabel =
    state.currency === "bfusd" ? "USDT" : state.currency?.toUpperCase();

  const formik = useFormik({
    initialValues: {
      amount: state.amount,
      userWallet,
      selectedCryptoWallet: state.selectedCryptoWallet,
      fees,
      agreement1: false,
    },
    onSubmit: (values) =>
      setState({
        ...state,
        amount: values.amount ?? "0",
        step: WithdrawalProcessSteps.WITHDRAWAL_SUMMARY,
      }),
    validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (!formik.isValid) {
      animateScrollTo(undefined, document.getElementById("withdrawform"), 20);
    }
  }, [formik.isSubmitting]);

  const hasAccessToWithdrawal =
    bankFormAddressDataAvailable &&
    ["verified", "fully_verified"].includes(verificationStatus);

  if (!hasAccessToWithdrawal) {
    return <AccessRestrictedMessage isCompany={isCompany} />;
  }

  if (bankAccounts.length === 0) {
    return (
      <div className={style.fiatForm}>
        <Alert
          message="Note!"
          showIcon
          icon={<DangerSVG />}
          description="You have not registered a bank account yet. Please submit a
              request below to add your Bank Account. Note we are only able
              to process withdrawals/deposits to/from one account in your
              name."
        />
        <BankForm
          values={values}
          errors={errors}
          investorAccountDetails={investorAccountDetails}
        />
      </div>
    );
  }

  const onActionBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: "edit",
      payload: {
        value: e.target.value,
        name: e.target.name,
      },
      relatedData: [
        {
          name: "selectedWalletType",
          value: e.target.getAttribute("data-usdtype") || undefined,
        },
        {
          name: "selectedWalletCountry",
          value: e.target.getAttribute("data-country") || undefined,
        },
        {
          name: "selectedWalletCurrency",
          value: e.target.getAttribute("data-currency") || undefined,
        },
      ],
    });
  };

  const renderTransactionFees = () => {
    const fiatTransactionFee =
      Number(formik.values.amount) <= 10_000
        ? 100
        : Number(formik.values.amount) * 0.01;

    const fiatSelectedWalletFeeObject = fees?.find(
      (fee: any) => fee.currency === currency
    );

    const currencyLabel =
      currency === "bfusd" ? "USDT" : currency?.toLocaleUpperCase();

    return (
      <div className={style.transactionFees}>
        <div>
          <span>Withdrawal Fee:&nbsp;</span>
          {`${Big(fiatTransactionFee).round(2)} ${currencyLabel}`}
        </div>

        <div>
          <span>Minimum Amount:&nbsp;</span>
          {`${Big(fiatSelectedWalletFeeObject?.minimum_amount ?? 0).round(
            2
          )} ${currencyLabel}`}
        </div>
      </div>
    );
  };

  return (
    <Form className={style.fiatForm} onFinish={formik.handleSubmit}>
      <div className={style.withdrawAmountWrapper}>
        {!isMobile && renderTransactionFees()}

        <Form.Item help={formik.errors.amount} label="Amount to withdraw">
          {isMobile && renderTransactionFees()}

          <Input
            suffix={
              <Space>
                <CurrencyAvatar
                  currency={currency}
                  background={
                    avatarBgColor[currency] ?? avatarBgColor["default"]
                  }
                />
                {currencyLabel}
              </Space>
            }
            placeholder="Enter amount"
            {...formik.getFieldProps("amount")}
            autoComplete="off"
            onChange={async (e) => {
              let value = e.target.value;

              if (value.startsWith(".") || value.startsWith(",")) {
                value = `0${value}`;
              }

              if (!/^\d*\.?\d*$/.test(value)) {
                return;
              }

              await formik.setFieldValue("amount", value);
              setState({
                ...state,
                amount: value,
              });
            }}
          />
        </Form.Item>
      </div>

      {isBftCurrency && <BftNotice />}
      {bankAccounts.map((bankAccount) => {
        const checked =
          String(bankAccount.id) === String(values.selectedWallet);

        const fee = getWalletFee(
          state.walletFeeObject,
          Number(formik.values.amount) || 0
        );
        const accountBlocked =
          fiatRestrictedCountries.includes(bankAccount.country) ||
          fiatRestrictedCountries.includes(countryOfResidence);

        return (
          <BankAccountOption
            key={bankAccount.id}
            bankAccount={bankAccount}
            fee={fee}
            accountBlocked={accountBlocked}
            checked={checked}
            onActionBoxChange={(e) => {
              onActionBoxChange(e);
              setState((prev) => ({
                ...prev,
                selectedCryptoWallet: bankAccount,
              }));
            }}
          />
        );
      })}

      <Space direction="vertical" size={"large"}>
        <Alert
          showIcon
          message="Withdrawal restriction by country"
          icon={<DangerSVG />}
          description={[
            fdtWithdrawalRestrictedCountries(blockedCountries),
            fdtConsiderStableCoins,
          ]}
        />

        <Alert
          icon={<DangerSVG />}
          showIcon
          message="Fiat Withdrawal and Documentation Requirements"
          description={
            <>
              <ol>
                <li>
                  <b>Conversion and Currency Risk:</b>
                  <br />
                  Please be aware that initiating a fiat withdrawal involves
                  converting your USDT balance to USD before the withdrawal is
                  executed. The conversion rate between USDT and USD may vary,
                  as it is based on the prevailing market rate, and the exact
                  USD amount will be determined at the time of processing. By
                  proceeding, you acknowledge and accept any currency risk
                  associated with this USDT to USD conversion.
                </li>
                <li>
                  <b>Processing in USD:</b>
                  <br />
                  All fiat withdrawals are processed in USD. Please confirm with
                  your bank whether they accept USD and how they process USD
                  deposits, as BF Virtual Assets Broker is not responsible for
                  any additional currency risk, conversion charges into your
                  local currency, or potential losses if funds are returned to
                  us minus fees.
                </li>
                <li>
                  <b>Required Documentation for Processing:</b>
                  <br />
                  After your withdrawal request is received, BF’s partner bank
                  will require the following documents for verification and
                  processing:
                  <ul>
                    <li>
                      Relationship Proof verifying your connection with BF
                      Virtual Assets. You can{" "}
                      <a
                        href="https://signnow.com/s/ctXmFq1P?name_formula=RelationshipProof%20-%20FDT%20-%20BF%20Virtual%20Assets%20%7CFull%20Name%201%7CDate%20and%20Time%201"
                        target="_blank"
                      >
                        sign the document here
                      </a>
                      .
                    </li>
                    <li>
                      Latest Proof of Address (POA) and Proof of Identity (POI)
                      dates within the last 2 months.
                    </li>
                  </ul>
                </li>
              </ol>
              <p>
                BF’s customer service team will reach out to assist you in
                collecting these documents. Please anticipate possible delays as
                we transmit these documents to our partner bank.
              </p>
            </>
          }
        />
      </Space>
      <div className={style.agreementsWrapper}>
        <Form.Item
          required
          hasFeedback
          help={formik.touched.agreement1 ? formik.errors.agreement1 : false}
          className={style.label}
        >
          <div className={style.checkboxWraper}>
            <Checkbox
              {...formik.getFieldProps("agreement1")}
              checked={formik.values.agreement1}
              id="agreement1"
            >
              <Typography.Text>
                <label htmlFor="agreement1" className={style.label}>
                  I confirm that my personal information, including name and
                  address, as currently shown in the Account section, is
                  accurate and up-to-date.
                </label>
              </Typography.Text>
            </Checkbox>
          </div>
        </Form.Item>
      </div>
      <NavigationFooter
        onCancel={() =>
          setState({ ...state, step: WithdrawalProcessSteps.SELECT_METHOD })
        }
        onContinue={() => {
          formik.submitForm();
        }}
        disabled={!formik.isValid || !formik.values.agreement1}
      />
    </Form>
  );
};

const mapStateToProps = (store: TReducers, ownProps: FiatFormProps) => {
  return {
    userWallet: (store.fundsReducer.supportedUserWallets || []).filter(
      (el) => el.currency === ownProps.currency
    )[0],
    isCompany: !!store.companyReducer.companyDetails.investor_id,
    bankFormAddressDataAvailable:
      store.fundsReducer.bankFormAddressDataAvailable,
    countryOfResidence: store.userInfo.country,
    fiatRestrictedCountries: store.appSettings.fiat_restricted_countries,
    verificationStatus: store.userInfo.verification_status,
    bankAccounts: store.fundsReducer.bankAccounts,
    investorId: store.userInfo.investor_id,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    setBankFormDataAvailable: (data: boolean) => {
      dispatch(setBankFormDataAvailable(data));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FiatForm) as any;
